import { useState, useMemo } from 'react';
import { useTwilioNumbers, type TwilioPhoneNumber } from './useTwilioNumbers';
import { useAuth } from './useAuth';

export interface CallerIdOption {
  id: string;
  phoneNumber: string;
  friendlyName: string | null;
  isAssigned: boolean;
}

const STORAGE_KEY = 'selected-caller-id';

export function useCallerIdOptions() {
  const { user } = useAuth();
  const { data: numbers } = useTwilioNumbers();

  const [selectedId, setSelectedIdRaw] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });

  const setSelectedId = (id: string | null) => {
    setSelectedIdRaw(id);
    try {
      if (id) localStorage.setItem(STORAGE_KEY, id);
      else localStorage.removeItem(STORAGE_KEY);
    } catch { /* noop */ }
  };

  const options = useMemo(() => {
    if (!numbers || !Array.isArray(numbers)) return [];
    return (numbers as TwilioPhoneNumber[])
      .filter(n => n.status === 'active' && n.capabilities?.voice)
      .map(n => ({
        id: n.id,
        phoneNumber: n.phone_number,
        friendlyName: n.friendly_name,
        isAssigned: n.assigned_user_id === user?.id,
      }))
      .sort((a, b) => {
        if (a.isAssigned && !b.isAssigned) return -1;
        if (!a.isAssigned && b.isAssigned) return 1;
        return 0;
      });
  }, [numbers, user?.id]);

  // Auto-select: persisted choice > assigned number > first available
  const effectiveId = useMemo(() => {
    if (!options.length) return null;
    if (selectedId && options.find(o => o.id === selectedId)) return selectedId;
    const assigned = options.find(o => o.isAssigned);
    if (assigned) return assigned.id;
    return options[0].id;
  }, [options, selectedId]);

  return { options, selectedId: effectiveId, setSelectedId };
}
